import React, { useEffect, useState } from 'react';
import { auth, firestore } from '../utils/firebaseConfig';
import { signOut } from 'firebase/auth';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { Navigate, useNavigate } from 'react-router-dom';
import Sidebar from './Sidebar';
import themes from '../utils/themes';
import '../styles/Frame.css';

const DEFAULT_THEME = 'qf-light';

const applyTheme = (themeName) => {
    const resolvedTheme = themes[themeName] ? themeName : DEFAULT_THEME;
    Object.entries(themes[resolvedTheme]).forEach(([key, value]) => {
        document.documentElement.style.setProperty(key, value);
    });
    return resolvedTheme;
};

const Frame = ({ children, managerOnly = false, locationsOnly = false }) => {
    const [userInfo, setUserInfo] = useState(null);
    const [profileLoading, setProfileLoading] = useState(true);
    const [profileError, setProfileError] = useState('');
    const [showRegistration, setShowRegistration] = useState(false);
    const [showSidebar, setShowSidebar] = useState(true);
    const [tempFirstName, setTempFirstName] = useState('');
    const [tempLastName, setTempLastName] = useState('');
    const [tempContactEmail, setTempContactEmail] = useState('');
    const [selectedTheme, setSelectedTheme] = useState(DEFAULT_THEME);
    const [tempTheme, setTempTheme] = useState(DEFAULT_THEME);
    const [tempLocationsEnabled, setTempLocationsEnabled] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        const user = auth.currentUser;
        if (!user) {
            applyTheme(DEFAULT_THEME);
            setProfileLoading(false);
            return undefined;
        }

        return onSnapshot(doc(firestore, 'users', user.uid), (snapshot) => {
            if (snapshot.exists()) {
                const profile = snapshot.data();
                const resolvedTheme = applyTheme(profile.theme || DEFAULT_THEME);
                setUserInfo(profile);
                setSelectedTheme(resolvedTheme);
                setTempTheme(resolvedTheme);
                setTempLocationsEnabled(profile.locationsEnabled === true);
                setShowRegistration(false);
            } else {
                const resolvedTheme = applyTheme(DEFAULT_THEME);
                setSelectedTheme(resolvedTheme);
                setTempTheme(resolvedTheme);
                setShowRegistration(true);
            }
            setProfileError('');
            setProfileLoading(false);
        }, (error) => {
            console.error('Error loading user profile:', error);
            setProfileError('Unable to load your profile. Please sign out and try again.');
            setProfileLoading(false);
        });
    }, []);

    useEffect(() => {
        const handleResize = () => setShowSidebar(window.innerWidth > 768);
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleThemeChange = (event) => {
        const nextTheme = applyTheme(event.target.value);
        setTempTheme(nextTheme);
    };

    const openRegistrationPopup = () => {
        setTempFirstName(userInfo?.firstName || '');
        setTempLastName(userInfo?.lastName || '');
        setTempContactEmail(userInfo?.contactEmail || '');
        setTempTheme(selectedTheme);
        setTempLocationsEnabled(userInfo?.locationsEnabled === true);
        setShowRegistration(true);
    };

    const handleRegistration = async (event) => {
        event.preventDefault();
        const user = auth.currentUser;
        if (!user) return;

        try {
            const profilePatch = {
                firstName: tempFirstName.trim(),
                lastName: tempLastName.trim(),
                contactEmail: tempContactEmail.trim(),
                email: user.email,
                theme: tempTheme,
            };
            if (userInfo?.manager === true) profilePatch.locationsEnabled = tempLocationsEnabled;

            await setDoc(doc(firestore, 'users', user.uid), profilePatch, { merge: true });
            setUserInfo((current) => ({ ...current, ...profilePatch }));
            setSelectedTheme(tempTheme);
            setShowRegistration(false);
            setProfileError('');
        } catch (error) {
            console.error('Error saving user profile:', error);
            setProfileError('Unable to save your profile. Please try again.');
        }
    };

    const handleCancel = () => {
        applyTheme(selectedTheme);
        setTempTheme(selectedTheme);
        setTempLocationsEnabled(userInfo?.locationsEnabled === true);
        setShowRegistration(false);
    };

    const handleNav = (path) => {
        navigate(path);
        if (window.innerWidth <= 768) setShowSidebar(false);
    };

    const handleLogOut = async () => {
        await signOut(auth);
        navigate('/');
    };

    if (profileLoading) return <div className="app-status" role="status">Loading profile…</div>;
    if (managerOnly && userInfo?.manager !== true) return <Navigate to="/dashboard" replace />;
    if (locationsOnly && !(userInfo?.manager === true && userInfo?.locationsEnabled === true)) {
        return <Navigate to="/dashboard" replace />;
    }

    return (
        <div className="frame">
            <button type="button" className="menu-button" onClick={() => setShowSidebar((visible) => !visible)} aria-label={showSidebar ? 'Close navigation' : 'Open navigation'} aria-expanded={showSidebar}>☰</button>

            <div className={`sidebar-container ${showSidebar ? 'visible' : ''}`}>
                <Sidebar userInfo={userInfo} openRegistrationPopup={openRegistrationPopup} handleNav={handleNav} handleLogOut={handleLogOut} />
            </div>

            <main className="content" onClick={() => window.innerWidth <= 768 && setShowSidebar(false)}>
                {profileError && <p className="error" role="alert">{profileError}</p>}
                {children}
            </main>

            {showRegistration && (
                <div className="registration-popup" role="dialog" aria-modal="true" aria-labelledby="profile-title">
                    <div className="popup-header">
                        <h2 id="profile-title">Edit Your Profile</h2>
                        <button type="button" className="close-button" onClick={handleCancel} aria-label="Close profile editor">×</button>
                    </div>
                    <form onSubmit={handleRegistration}>
                        <label>First name<input type="text" value={tempFirstName} onChange={(event) => setTempFirstName(event.target.value)} required /></label>
                        <label>Last name<input type="text" value={tempLastName} onChange={(event) => setTempLastName(event.target.value)} required /></label>
                        <label>Contact email<input type="email" value={tempContactEmail} onChange={(event) => setTempContactEmail(event.target.value)} /></label>
                        <label htmlFor="theme-select">Theme</label>
                        <select id="theme-select" value={tempTheme} onChange={handleThemeChange}>
                            {Object.keys(themes).map((themeName) => <option key={themeName} value={themeName}>{themeName}</option>)}
                        </select>
                        {userInfo?.manager === true && (
                            <label className="toggle-setting">
                                <span><strong>Locations?</strong><small>Show the prepared Locations dashboard.</small></span>
                                <input type="checkbox" checked={tempLocationsEnabled} onChange={(event) => setTempLocationsEnabled(event.target.checked)} />
                                <span className="toggle-track" aria-hidden="true"><span /></span>
                            </label>
                        )}
                        <div className="modal-buttons"><button type="submit">Save</button><button type="button" onClick={handleCancel}>Cancel</button></div>
                    </form>
                </div>
            )}
        </div>
    );
};

export default Frame;
